import React, { useContext } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import { Paragraph } from '../../styles/paragraph';
import { Component as AddToCartButton } from '../AddToCartButton';
import { ProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import VipBoxButton from '../VipBoxButton/VipBoxButtonContainer';

const ActionButtons = styled.div`
  margin-bottom: ${props => props.theme.spacing.small}px;
`;

const PreorderButton = styled(BasicButton).attrs({
  fullWidth: true,
})`
  margin-bottom: ${props => props.theme.spacing.moderate}px;
`;

const ProductDetailAddToCartButton = styled(AddToCartButton)`
  ${({ solidDisabledColor }) =>
    solidDisabledColor &&
    `&:disabled {
      background-color: ${({ theme }) => theme.colors.lavender200};
      opacity: 1;
    }
  `}
`;

const StyledParagraph = styled(Paragraph)`
  font-size: 12px;
  font-weight: normal;
  line-height: 1.33;
  padding-bottom: ${props => props.theme.spacing.tiny}px;
  text-align: center;

  ${desktop`
    text-align: left;
  `}
`;

const ButtonWrapper = styled.div``;

const ADD_TO_BAG = 'ADD_TO_BAG';
const COMING_SOON = 'COMING_SOON';
const PREORDER = 'PREORDER';
const PREORDER_BORDERFREE = 'PREORDER_BORDERFREE';
const WAITLIST_EMAIL = 'WAITLIST_EMAIL';
const XTRA_VIP_BOX_NOTIFICATION = 'XTRA_VIP_BOX_NOTIFICATION';

function ProductDetailCTA({
  allowExclusiveCheckout = false,
  allowOutOfStockCheckout = false,
  availableQuantity,
  cartButtonDisabled,
  className,
  children,
  constructorButtonLabel,
  isExclusive = false,
  isPreorderable,
  isSoldOut = false,
  isComingSoonProduct = false,
  isWaitlistable = true,
  isXtraVipBox = false,
  onAddToCart,
  onWaitlistClick,
  ...rest
}) {
  const {
    singleCTA: { enabled: singleCTAFlagEnabled },
  } = useContext(ProductDetailContext);
  const isBorderfreeCustomer = useSelector(
    state => state.borderfree.isBorderfreeCustomer
  );
  const isSizeSoldOut = availableQuantity <= 0;

  let action = ADD_TO_BAG;
  if (isPreorderable && !isBorderfreeCustomer) {
    action = PREORDER;
  } else if (isPreorderable && isBorderfreeCustomer) {
    action = PREORDER_BORDERFREE;
  } else if (!isBorderfreeCustomer && isXtraVipBox && isSoldOut) {
    action = XTRA_VIP_BOX_NOTIFICATION;
  } else if (
    !isBorderfreeCustomer &&
    isSizeSoldOut &&
    isWaitlistable &&
    !isPreorderable
  ) {
    action = WAITLIST_EMAIL;
  } else if (isComingSoonProduct) {
    action = COMING_SOON;
  }

  switch (action) {
    case PREORDER:
      return (
        <ActionButtons className={className}>
          <PreorderButton
            onClick={onAddToCart}
            disabled={cartButtonDisabled}
            data-autotag="pre-order-btn"
            {...rest}
          >
            <FormattedMessage
              id="site_pdp.preorder_cta"
              defaultMessage="Pre-order"
            />
          </PreorderButton>
        </ActionButtons>
      );
    case PREORDER_BORDERFREE:
      return (
        <ActionButtons className={className}>
          <ButtonWrapper>
            <ProductDetailAddToCartButton
              disabled
              solidDisabledColor={singleCTAFlagEnabled}
              data-autotag="pdp-add-to-bag"
              {...rest}
            >
              {children}
            </ProductDetailAddToCartButton>
          </ButtonWrapper>
        </ActionButtons>
      );
    case WAITLIST_EMAIL:
      return (
        <ActionButtons className={className}>
          <PreorderButton
            onClick={onWaitlistClick}
            data-autotag="addtowaitlist-btn"
            {...rest}
          >
            <FormattedMessage
              id="site_pdp.add_to_waitlist_cta"
              defaultMessage="Add To Wait List"
            />
          </PreorderButton>
        </ActionButtons>
      );
    case XTRA_VIP_BOX_NOTIFICATION:
      return (
        <ActionButtons className={className}>
          <ButtonWrapper>
            <StyledParagraph>
              <FormattedMessage
                id="site_pdp.xtra_vip_message"
                defaultMessage="Sad it sold out? Be the first to know when the next VIP Pack drops!"
              />
            </StyledParagraph>
            <VipBoxButton />
          </ButtonWrapper>
        </ActionButtons>
      );
    case COMING_SOON:
      return (
        <ActionButtons className={className}>
          <ButtonWrapper>
            <ProductDetailAddToCartButton
              disabled
              solidDisabledColor={singleCTAFlagEnabled}
              data-autotag="pdp-add-to-bag"
              {...rest}
            >
              <FormattedMessage
                id="site_pdp.coming_soon_cta"
                defaultMessage="Item Coming Soon"
              />
            </ProductDetailAddToCartButton>
          </ButtonWrapper>
        </ActionButtons>
      );
    default:
      return (
        <ActionButtons className={className}>
          <ButtonWrapper>
            <ProductDetailAddToCartButton
              onClick={onAddToCart}
              disabled={cartButtonDisabled || isSoldOut}
              solidDisabledColor={singleCTAFlagEnabled}
              data-autotag="pdp-add-to-bag"
              data-cnstrc-btn={constructorButtonLabel}
              variant="primaryLavender"
              {...rest}
            >
              {children}
            </ProductDetailAddToCartButton>
          </ButtonWrapper>
        </ActionButtons>
      );
  }
}

ProductDetailCTA.propTypes = {
  allowExclusiveCheckout: PropTypes.bool,
  allowOutOfStockCheckout: PropTypes.bool,
  availableQuantity: PropTypes.number,
  cartButtonDisabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  isExclusive: PropTypes.bool,
  isPreorderable: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isComingSoonProduct: PropTypes.bool,
  isWaitlistable: PropTypes.bool,
  isXtraVipBox: PropTypes.bool,
  onAddToCart: PropTypes.func,
  onWaitlistClick: PropTypes.func,
};

ProductDetailCTA.ProductDetailAddToCartButton = ProductDetailAddToCartButton;

export default ProductDetailCTA;
